import { Table, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useGetExpensesQuery } from '../../slices/expensesApiSlice';
import { useState } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const ExpenseInvoiceSummary = () => {
  const [firstNumber, setFirstNumber] = useState('');
  const [lastNumber, setLastNumber] = useState('');
  const [showTable, setShowTable] = useState(false);
  const [error, setError] = useState('');
  const [invoiceSummary, setInvoiceSummary] = useState({});
  const [searchInvoice, setSearchInvoice] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [showZeroOnly, setShowZeroOnly] = useState(false);

  const { data: expensesData, isLoading } = useGetExpensesQuery({});

  const handleShowTable = () => {
    const start = Number(firstNumber);
    const end = Number(lastNumber);

    if (isNaN(start) || isNaN(end)) {
      setError('Please enter valid numbers.');
      setShowTable(false);
      return;
    }

    if (end <= start) {
      setError('Last number must be greater than first number.');
      setShowTable(false);
      return;
    }

    setError('');
    const summary = {};

    // Initialize all invoice numbers in range
    for (let i = start; i <= end; i++) {
      summary[i.toString()] = {
        category: '',
        total: 0,
        seen: false,
        date: '',
        month: '',
      };
    }

    // Process expenses
    expensesData?.expenses?.forEach(expense => {
      const notes = expense.notes?.trim();
      const amount = Number(expense.amount || 0);
      const category = expense.category;
      const dateObj = new Date(expense.createdAt);
      const date = dateObj.toLocaleDateString();
      const month = dateObj.toLocaleString('default', { month: 'long', year: 'numeric' });

      if (notes && summary.hasOwnProperty(notes)) {
        const entry = summary[notes];
        entry.total += amount;
        if (!entry.seen) {
          entry.category = category;
          entry.date = date;
          entry.month = month;
          entry.seen = true;
        }
      }
    });

    setInvoiceSummary(summary);
    setShowTable(true);
  };

  const handleExport = () => {
    const filtered = Object.entries(invoiceSummary).filter(([invoice, entry]) => {
      const matchesInvoice = invoice.toLowerCase().includes(searchInvoice.toLowerCase());
      const matchesCategory = entry.category.toLowerCase().includes(searchCategory.toLowerCase());
      const isZero = entry.total === 0;
      return matchesInvoice && matchesCategory && (showZeroOnly ? isZero : true);
    });

    const rows = filtered.map(([invoice, entry]) => ({
      Month: entry.month,
      Invoice: invoice,
      Date: entry.date,
      Category: entry.category,
      Total: entry.total,
    }));

    const totalSum = rows.reduce((sum, row) => sum + row.Total, 0);
    rows.push({ Month: '', Invoice: 'Total', Date: '', Category: '', Total: totalSum });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice Summary');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `Invoice_Summary_${firstNumber}_to_${lastNumber}.xlsx`);
  };

  const filteredSummary = Object.entries(invoiceSummary).filter(([invoice, entry]) => {
    const matchesInvoice = invoice.toLowerCase().includes(searchInvoice.toLowerCase());
    const matchesCategory = entry.category.toLowerCase().includes(searchCategory.toLowerCase());
    const isZero = entry.total === 0;
    return matchesInvoice && matchesCategory && (showZeroOnly ? isZero : true);
  });

  const groupedByMonth = {};
  filteredSummary.forEach(([invoice, entry]) => {
    if (!groupedByMonth[entry.month]) {
      groupedByMonth[entry.month] = [];
    }
    groupedByMonth[entry.month].push({ invoice, ...entry });
  });

  return (
    <div className="container mt-4">
      <h1 className="text-center">Invoice Category Summary</h1>

      <Form className="mb-3 d-flex justify-content-center flex-wrap">
        <Form.Group className="mx-2">
          <Form.Label>First Invoice Number:</Form.Label>
          <Form.Control
            type="number"
            value={firstNumber}
            onChange={e => setFirstNumber(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mx-2">
          <Form.Label>Last Invoice Number:</Form.Label>
          <Form.Control
            type="number"
            value={lastNumber}
            onChange={e => setLastNumber(e.target.value)}
          />
        </Form.Group>
        <Form.Group className="mx-2">
          <Form.Label>Search Invoice:</Form.Label>
          <Form.Control
            type="text"
            value={searchInvoice}
            onChange={e => setSearchInvoice(e.target.value)}
            placeholder="e.g. 1052"
          />
        </Form.Group>
        <Form.Group className="mx-2">
          <Form.Label>Search Category:</Form.Label>
          <Form.Control
            type="text"
            value={searchCategory}
            onChange={e => setSearchCategory(e.target.value)}
            placeholder="e.g. Transport"
          />
        </Form.Group>
        <div className="d-flex align-items-end mx-2">
          <Button variant="primary" onClick={handleShowTable} disabled={isLoading}>
            {isLoading ? <Spinner animation="border" size="sm" /> : 'Show Table'}
          </Button>
        </div>
        <div className="d-flex align-items-end mx-2">
          <Button
            variant={showZeroOnly ? 'warning' : 'outline-warning'}
            onClick={() => setShowZeroOnly(!showZeroOnly)}
          >
            {showZeroOnly ? 'Show All' : 'Show Only Zero Totals'}
          </Button>
        </div>
      </Form>

      {error && <Alert variant="danger" className="text-center">{error}</Alert>}

      {showTable && (
        <>
          {Object.entries(groupedByMonth).map(([month, entries]) => (
            <div key={month} className="mb-4">
              <h4 className="text-center">{month}</h4>
              <Table striped bordered hover responsive className="table-responsive">
                <thead className="text-center">
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody className="text-center">
                  {entries.map(entry => (
                    <tr key={entry.invoice}>
                      <td>{entry.invoice}</td>
                      <td>{entry.date || '-'}</td>
                      <td>{entry.category || '-'}</td>
                      <td>{entry.total.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="fw-bold">
                    <td>Total</td>
                    <td></td>
                    <td></td>
                    <td>
                      {entries
                        .reduce((sum, entry) => sum + entry.total, 0)
                        .toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          ))}

          <div className="text-center mt-3">
            <Button variant="success" onClick={handleExport}>
              Export to Excel
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default ExpenseInvoiceSummary;